import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { toast } from 'sonner';

import { useAuth } from '@/lib/auth/auth-provider';

import { SignUpDialog } from './SignUpDialog';

/**
 * Human-readable copy for each `bc_error` query param the OAuth
 * callback Lambda redirects with on failure. Anything not in the map
 * falls through to a generic message.
 */
const BC_ERROR_COPY: Record<string, string> = {
  missing_oauth_params:
    "BigCommerce didn't return enough information to complete the install. Try again from your BigCommerce store admin.",
  bc_token_exchange_failed:
    'BigCommerce rejected our credential exchange. Try the install again from your BigCommerce store admin.',
  misconfigured_secret:
    "We're still finishing setup on our side. Try again in a few minutes or contact support.",
  secret_fetch_failed:
    'We hit a transient error completing the install. Try again from your BigCommerce store admin.',
  kms_encrypt_failed:
    'We hit a transient error completing the install. Try again from your BigCommerce store admin.',
  bc_context_invalid:
    'BigCommerce returned an invalid store reference. Try the install again from your BigCommerce store admin.',
  db_error:
    "We couldn't save your connection. Try the install again from your BigCommerce store admin.",
  connection_not_found:
    "We couldn't find your Mailchute installation for that store. Re-install Mailchute from your BigCommerce store admin to reconnect.",
};

/**
 * `/signup` — mounts the sign-up wizard on step 1.
 *
 * Also acts as the landing for the BC-initiated install flow (ADR
 * 0011): the OAuth callback redirects merchants here with a
 * `?bc_claim={connection_id}` query param. We branch on auth state:
 *
 *   - Already signed in + bc_claim present → redirect straight to
 *     `/app/bc-claim?id=…` (skip signup, go directly to claim).
 *   - Not signed in + bc_claim present → show the SignUpDialog (banner
 *     explains the BC context) and pass the claim id through to
 *     `onComplete`, which routes to `/app/bc-claim?id=…` instead of
 *     the default `/app/onboarding/step/1`.
 *   - Not signed in + no bc_claim → standard signup.
 */
export function SignUpPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { state } = useAuth();
  const bcClaim = searchParams.get('bc_claim');
  const bcError = searchParams.get('bc_error');

  // Already-signed-in user landing here from a BC install shortcuts
  // straight to the claim handler — no need to re-show signup.
  useEffect(() => {
    if (bcClaim && state.status === 'authenticated') {
      void navigate(`/app/bc-claim?id=${encodeURIComponent(bcClaim)}`, { replace: true });
    }
  }, [bcClaim, state.status, navigate]);

  // Surface OAuth callback failures (the callback redirects to
  // /signup?bc_error=… on any error). Toast once, then strip the
  // param so refresh doesn't re-fire.
  useEffect(() => {
    if (!bcError) return;
    const copy =
      BC_ERROR_COPY[bcError] ??
      "We couldn't complete the BigCommerce install. Try again from your BigCommerce store admin.";
    toast.error(copy);
    const next = new URLSearchParams(searchParams);
    next.delete('bc_error');
    setSearchParams(next, { replace: true });
  }, [bcError, searchParams, setSearchParams]);

  return (
    <main className="bg-muted/50 min-h-screen">
      {/* TODO: replace with <BlurredDashboard /> once the dashboard exists.
          Until then this stays as a neutral surface — the modal is what
          the user is looking at. */}
      <SignUpDialog
        initialStep="form"
        bcClaimNotice={bcClaim !== null}
        onComplete={() => {
          if (bcClaim) {
            void navigate(`/app/bc-claim?id=${encodeURIComponent(bcClaim)}`);
            return;
          }
          // Standard fresh signup — straight into the wizard.
          void navigate('/app/onboarding/step/1');
        }}
      />
    </main>
  );
}
